import React, { useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { 
  Box, 
  Stack, 
  Typography, 
  Dialog
} from '@mui/material'

import Checkbox from 'packages/ui/Checkbox'
import ModalHeader from 'components/global/Modals/ModalHeader'
import { TelInput } from 'components/global/CUK/TelInput'
import { matchIsValidTel } from '@center/ui-kit'

import { useNavigate } from 'router'
import { useSites, useUser } from 'store'
import useSave, { DEFAULT_COUPON, buildSchema } from './use-save'
import AccountsPerimeter from './AccountsPerimeter'
import ErrorMessage from './ErrorMessage'
import FieldLabel from './FieldLabel'
import NumberInput from './NumberInput'
import DurationField from './DurationField'
import OffersField from './OffersField'
import { trackEvent } from 'analytics'

import {
  StyledDialog,
  StyledPaper,
  FormContainer,
  FieldStack,
  RowStack,
  StyledTextArea,
  ButtonContainer,
  CancelButton,
  SubmitButton,
  CheckboxContainer,
  StyledCheckbox
} from './CreateCouponStyles'

const getSingleSite = (accounts) => {
  const sites = (accounts || []).filter(({is_site}) => is_site)
  return sites.length === 1 ? sites[0] : null
}

const getSiteSelection = (accounts, site) => {
  if (!site) {
    return []
  }
  return site.hierarchie.map(sf_id => (accounts || []).find(account => account.sf_id === sf_id))
}

const CreateCoupon: React.FC = () => {
  const navigate = useNavigate()
  const { save, loading } = useSave()
  const { t } = useTranslation()
  const [user] = useUser()
  const [{ selected, accounts }] = useSites()
  const defaultSelection = useMemo(() => {
    const selectedSite = getSingleSite(selected)
    if (selected.length) {
      return {
        site: selectedSite,
        accounts: selectedSite ? accounts : []
      }
    }
    const site = getSingleSite(user?.accounts)
    return {
      site,
      accounts: getSiteSelection(user?.accounts, site)
    }
  }, [accounts, selected, user])

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(buildSchema(t)),
    defaultValues: {
      ...DEFAULT_COUPON,
      site_id: defaultSelection.site?.sf_id
    }
  })

  const { control, handleSubmit, watch, setValue, clearErrors, formState: { dirtyFields, errors } } = methods
  const offerId = watch('offer_id')

  const onClose = () => {
    navigate({ pathname: '..' })
  }

  const onSubmit = async (submitData) => {
    const success = await save(submitData)
    if (success) {
      trackEvent('create_code', {
        quantity: submitData.amount,
        has_offer: Boolean(submitData.offer_id),
        simultaneous_connections: submitData.max_usage,
        give_private_wifi_access: submitData.with_ran,
      })
      onClose()
    }
  }

  const getErrorMessage = (error): string | undefined => {
    if (typeof error?.message === 'string') {
      return error.message
    }
    return undefined
  }

  return (
    <StyledDialog>
      <Dialog
        open
        PaperProps={{
          sx: {
            borderRadius: '24px',
            width: 480,
            maxWidth: 480
          }
        }}
      >
        <StyledPaper>
          <ModalHeader
            subtitle={t('coupons.create.subtitle')}
            title={t('coupons.create.title')}
            onClose={onClose}
          />
          
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <FormContainer>
                <FieldStack>
                  <Box>
                    <FieldLabel labelKey="coupons.create.site_selection" />
                    <Controller
                      control={control}
                      name="site_id"
                      render={({ field: { onChange } }) => (
                        <Box sx={{ maxWidth: '432px' }}>
                          <AccountsPerimeter
                            initialSelection={defaultSelection.accounts}
                            onChange={(site) => {
                              setValue('offer', null)
                              onChange(site?.sf_id)
                            }}
                            onValidationClear={() => clearErrors('site_id')}
                            hasError={Boolean(errors.site_id)}
                            checkBoxComponent={(props) => <Checkbox {...props} />}
                          />
                        </Box>
                      )}
                    />
                    <ErrorMessage message={getErrorMessage(errors.site_id)} />
                  </Box>

                  <RowStack>
                    <Box sx={{ width: '35%' }}>
                      <FieldLabel 
                        labelKey="coupons.create.number_of_codes" 
                        tooltip="coupons.create.number_of_codes_tooltip" 
                      />
                      <Controller
                        control={control}
                        name="amount"
                        render={({ field: { value, onChange } }) => (
                          <NumberInput
                            value={value}
                            onChange={(quantity) => {
                              onChange(quantity)
                              trackEvent('change_code_quantity', { quantity })
                            }}
                            hasError={Boolean(errors.amount)}
                          />
                        )}
                      />
                      <ErrorMessage message={getErrorMessage(errors.amount)} />
                    </Box>
                    
                    <Box sx={{ width: '65%' }}>
                      <DurationField />
                    </Box>
                  </RowStack>

                  <OffersField />

                  <Box>
                    <FieldLabel 
                      labelKey="coupons.create.max_simultaneous_connections" 
                      tooltip="coupons.create.max_simultaneous_connections_tooltip" 
                    />
                    <Controller
                      control={control}
                      name="max_usage"
                      render={({ field: { value, onChange } }) => (
                        <NumberInput
                          value={value}
                          onChange={(simultaneousConnections) => {
                            onChange(simultaneousConnections)
                            trackEvent('change_simultaneous_connections', {
                              simultaneous_connections: simultaneousConnections,
                            })
                          }}
                          disabled={Boolean(offerId)}
                          hasError={Boolean(errors.max_usage)}
                        />
                      )}
                    />
                    <ErrorMessage message={getErrorMessage(errors.max_usage)} />
                  </Box>

                  <RowStack>
                    <Box sx={{ width: '100%' }}>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 500, color: '#333' }}>
                          {t('coupons.create.phone')}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#989898' }}>
                          ({t('coupons.create.optional')})
                        </Typography>
                      </Stack>
                      <Controller
                        control={control}
                        name="phone_number"
                        render={({ field }) => (
                          <TelInput
                            size="small"
                            defaultCountry="FR"
                            {...field}
                            onChange={(value) => {
                              field.onChange(value)
                              setValue('send_sms', value.trim().length > 0 && matchIsValidTel(value.trim()))
                            }}
                            onBlur={() => {
                              field.onBlur()
                              if (dirtyFields.phone_number) {
                                trackEvent('change_code_phone', {
                                  has_phone: Boolean(field.value?.trim()),
                                })
                              }
                            }}
                          />
                        )}
                      />
                      <ErrorMessage message={getErrorMessage(errors.phone_number)} />
                    </Box>
                  </RowStack>

                  <Box>
                    <FieldLabel labelKey="coupons.create.description" />
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <StyledTextArea
                          {...field}
                          multiline
                          placeholder={t('coupons.create.description_placeholder')}
                          hasError={Boolean(errors.description)}
                        />
                      )}
                    />
                    <ErrorMessage message={getErrorMessage(errors.description)} />
                  </Box>

                  <Stack spacing={1.5}>
                    <Controller
                      control={control}
                      name="send_sms"
                      render={({ field: { value, onChange } }) => (
                        <CheckboxContainer>
                          <StyledCheckbox>
                            <Checkbox 
                              checked={value} 
                              onChange={() => {
                                const enabled = !value
                                onChange(enabled)
                                trackEvent('change_send_now', { enabled })
                              }}
                              size={24}
                            />
                          </StyledCheckbox>
                          <Typography sx={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                            {t('coupons.create.send_sms')}
                          </Typography>
                        </CheckboxContainer>
                      )}
                    />

                    <Controller
                      control={control}
                      name="with_ran"
                      render={({ field: { value, onChange } }) => (
                        <CheckboxContainer>
                          <StyledCheckbox>
                            <Checkbox 
                              checked={value} 
                              onChange={() => {
                                const enabled = !value
                                onChange(enabled)
                                trackEvent('change_give_private_wifi_access', { enabled })
                              }}
                              size={24}
                            />
                          </StyledCheckbox>
                          <Typography sx={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                            {t('coupons.create.wifi_access')}
                          </Typography>
                        </CheckboxContainer>
                      )}
                    />
                  </Stack>
                </FieldStack>
              </FormContainer>

              <ButtonContainer>
                <CancelButton onClick={onClose}>
                  <Typography sx={{ fontSize: '12px', color: '#333' }}>
                    {t('ui_elements.form_cancel')}
                  </Typography>
                </CancelButton>
                
                <SubmitButton
                  type="submit"
                  disabled={loading}
                  loading={loading}
                >
                  <Typography sx={{ fontSize: '12px' }}>
                    {loading ? t('ui_elements.loading_msg') : t('coupons.create.create_button')}
                  </Typography>
                </SubmitButton>
              </ButtonContainer>
            </form>
          </FormProvider>
        </StyledPaper>
      </Dialog>
    </StyledDialog>
  )
}

export default CreateCoupon
